/*
 * DocuSign — the shared model.
 *
 * The backend module is complete and every endpoint is live; this module owns
 * the domain rules for the whole envelope surface, not just the connection
 * badge.
 *
 * The rule that has not changed, and is the reason the file exists at all:
 * {@link signaturesNotBinding} is the ONE copy of the not-binding test — every
 * screen reuses it rather than re-deriving it. ⚠️ **What the test SAYS changed
 * on 2026-08-31** (*SUPERSEDED: "the warn-unless-provably-production test"*);
 * read its own comment before writing copy against it. The same
 * now goes for {@link TERMINAL_STATUSES}, delivery, and the two facts a UI has
 * to state about a signature — see the sections below.
 *
 * Nothing in this app ever talks to `account-d.docusign.com` or
 * `demo.docusign.net`. DocuSign is a confidential OAuth client with a private
 * RSA key; a browser cannot hold one. Every `/docusign/*` method in
 * `@/lib/api` calls OUR API, which holds the key and does the JWT grant.
 *
 * This module sits at the BOTTOM of the import graph — `@/lib/types` imports
 * from it, not the other way round — so everything here is structural, and the
 * envelope-shaped predicates are generic over "an object with these fields"
 * rather than over `DocuSignEnvelope`.
 */

/**
 * The environment the backend authenticates against.
 *
 * Not cosmetic: it is the same variable that picks the OAuth host, so it cannot
 * drift from reality the way a hand-written label can.
 *
 * Typed as the two known values, while every consumer below accepts a loose
 * `string | null | undefined` — an environment this build has never heard of
 * must be a value to fall back on, not a compile error on deploy day.
 */
export type DocuSignEnvironment = 'demo' | 'production';

/**
 * The two independent signals that decide the badge.
 *
 * Structural rather than importing `DocuSignConnection` from `@/lib/types`, so
 * this module stays at the bottom of the import graph and a partial or
 * hand-built object (a 502 with nothing in it) can be passed straight in.
 */
export interface DocuSignBindingSignals {
  environment?: string | null;
  account?: { baseUri?: string | null } | null;
}

/**
 * **Warn on a reading that is not production. Say nothing on no reading.** True
 * means we READ the connection and what came back was not a production one, so
 * every envelope it would send is worth nothing.
 *
 * ⚠️ **REVERSED 2026-08-31, on the owner's instruction, and the reversal is the
 * whole point of this comment.** *SUPERSEDED: "**Warn unless provably
 * production.** … **There is a state with no `environment` at all** — no
 * response. A 502, a timeout, an expired auth. 'Assume production, show
 * nothing' would be exactly wrong in the one moment we know least, so
 * `undefined` warns."*
 *
 * Two facts killed it, and neither is a matter of taste:
 *
 * 1. **`GET /docusign/connection` is ADMIN ONLY while sending takes any write
 *    role.** So "no reading" was never the rare 502 the old rule was written
 *    for — it was the ORDINARY case for the people who send. Every non-admin
 *    sender was told their signatures were worthless, on every quote, whatever
 *    the environment actually was.
 * 2. **DocuSign is on PRODUCTION now.** So the warning that fired on a null
 *    connection was not merely cautious, it was FALSE — and a warning that
 *    cries wolf on every send is one nobody reads on the day it is true.
 *
 * What survives unchanged, because it was never the part that was wrong:
 *
 * - **It is still not `=== 'demo'`.** A reading of a third environment nobody
 *   here has heard of is still not a production one, so it still warns. The old
 *   rule's second reason stands in full.
 * - **`account.baseUri` is still an INDEPENDENT second signal**, from
 *   DocuSign's own `/oauth/userinfo` rather than from our config, so a
 *   `production` environment on a demo host still warns.
 *
 * ⚠️ **What this costs, stated rather than buried: an admin whose connection
 * read FAILS now sees no warning where they used to see one.** That is the
 * instruction — never warn from an absence — and it is the reason the read
 * matters: this function can only be as honest as the answer it is given. If
 * the connection is unreadable by the people who send, fix the read, not this.
 */
export function signaturesNotBinding(connection: DocuSignBindingSignals | null | undefined): boolean {
  const environment = connection?.environment;
  // No reading at all — a 403, a 502, a timeout, or a caller that never asked.
  // Says nothing rather than guessing, in either direction.
  if (environment === null || environment === undefined || environment === '') return false;
  return environment !== 'production' || Boolean(connection?.account?.baseUri?.includes('demo'));
}

/**
 * The environment as a person reads it.
 *
 * An unrecognised or absent value is named as unknown rather than guessed at,
 * so the label never quietly implies either side of the question.
 *
 * ⚠️ **The two absences are no longer one case, and this label cannot tell them
 * apart.** *SUPERSEDED: "it is the state {@link signaturesNotBinding} treats
 * most cautiously".* Since 2026-08-31 an ABSENT environment warns about
 * nothing, while an UNRECOGNISED one still warns — so "Unknown environment" now
 * renders only where something really was read and was not a name we know. Do
 * not use this label to decide anything; ask {@link signaturesNotBinding}.
 */
export function docuSignEnvironmentLabel(environment?: string | null): string {
  switch (environment) {
    case 'production':
      return 'Production';
    case 'demo':
      return 'Demo (sandbox)';
    default:
      return 'Unknown environment';
  }
}

/* ── Status: DocuSign's own words, and why there is no enum ──────────────── */

/**
 * The statuses meaning "nothing further happens without somebody acting".
 *
 * **Mirrored from `TERMINAL_STATUSES` in the backend's
 * `src/modules/docusign/docusign.schema.ts`**, which is the source of truth —
 * the same frozen list drives the `outstanding` filter, the refusal on a second
 * signing URL, and the convert guard. If that list ever grows, this one has to
 * grow with it; nothing makes them agree automatically.
 *
 * ⚠️ **You should rarely need this.** Every envelope arrives with `terminal`
 * and `signed` already computed server-side, precisely so the common questions
 * do not need a client-side list. Reach for these constants when you hold a
 * bare status string and nothing else.
 */
export const TERMINAL_STATUSES = ['completed', 'declined', 'voided'] as const;

/** DocuSign's word for a fully signed envelope. */
export const COMPLETED_STATUS = 'completed';

/**
 * ⚠️ **`status` is DocuSign's own string, deliberately unconstrained, and it is
 * NOT a closed set.** `created`, `sent`, `delivered`, `signed`, `completed`,
 * `declined`, `voided` — and whatever DocuSign adds next.
 *
 * Typed as a plain `string` alias rather than a union, and the alias exists to
 * carry this warning to the point of use: **never write a `switch` over it with
 * no default**, and never treat an unrecognised word as an error. The backend
 * states the same rule in `docs/api/docusign.md`.
 *
 * The name says the other half: it is the status as of the last `/refresh`.
 * See {@link STATUS_IS_LAST_KNOWN_NOTE}.
 */
export type DocuSignLastKnownStatus = string;

/** True for a status in {@link TERMINAL_STATUSES}. Prefer the envelope's own `terminal`. */
export function isTerminalStatus(status: string | null | undefined): boolean {
  return (TERMINAL_STATUSES as readonly string[]).includes(status ?? '');
}

/** True only for a fully signed envelope. Prefer the envelope's own `signed`. */
export function isCompletedStatus(status: string | null | undefined): boolean {
  return status === COMPLETED_STATUS;
}

/**
 * ⚠️ **THERE IS STILL NO WEBHOOK.** DocuSign pushes nothing; every status this
 * app renders was written by a `POST /docusign/envelopes/{id}/refresh` that
 * something ran at some point.
 *
 * ⚠️ **What is NO LONGER true: that "something" is not only a person.** See
 * {@link BACKGROUND_CHECK_NOTE} — the server re-checks outstanding envelopes on
 * a timer. **The rule below is unchanged and the ARGUMENT under it moved**,
 * which is the dangerous kind of change: a rule kept for a reason that has
 * evaporated is the one somebody deletes next.
 *
 * So a stored `status` is a **last known** status and can be arbitrarily stale —
 * an envelope reading `sent` may have been signed an hour ago. It is no longer
 * stale *indefinitely*, but "the next pass is minutes away and is not
 * guaranteed" is not "live", and a screen may not present it as one.
 * `statusCheckedAt` is a separate column from `updatedAt` for exactly this
 * reason: it moves on every check including one that changed nothing, so "we
 * asked and it is still out" and "nothing has asked since Tuesday" stay
 * distinguishable. **A UI that cannot tell those apart quietly presents the
 * second as the first**, so show `statusCheckedAt` wherever a status is shown.
 */
export const STATUS_IS_LAST_KNOWN_NOTE =
  'As of the last check — DocuSign does not notify us. Refresh for the current state.';

/**
 * ⚠️ **A BACKGROUND POLL EXISTS, and much of this app's copy was written on the
 * belief that it does not.**
 *
 * Verified in the backend's `src/modules/docusign/docusign-status.worker.ts`
 * (2026-08-30), because a claim about server behaviour is the one part of a
 * contract nothing typechecks and this one had already been wrong once:
 *
 * - `startDocuSignStatusWorker` is called from `src/index.ts`, and
 *   `stopDocuSignStatusWorker` on shutdown.
 * - `TICK_INTERVAL_MS = 5 * 60_000`. It is a **floor, not a cadence** — the next
 *   tick is scheduled after the previous pass finishes.
 * - `BATCH_SIZE = 10`, taken by `findPollable` as the **least recently checked**
 *   outstanding envelopes (`ORDER BY status_checked_at ASC`). A backlog is not
 *   stranded, it simply takes more passes.
 * - `pollOutstandingEnvelopes` calls the SAME `refreshEnvelope` the route calls,
 *   so a `completed` envelope runs the SAME `onSignatureCompleted`: the quote
 *   moves to the accepting stage and the signed PDF is attached to the Xero
 *   quote. Nothing about the consequence differs by who triggered the check.
 *
 * ⚠️ **THE THREE CAVEATS ARE THE WHOLE REASON THIS IS A SENTENCE AND NOT A
 * NUMBER**, and dropping them replaces one false promise with another:
 *
 * 1. it **backs off to ~15 minutes** (`ERROR_BACKOFF_MS`) after a pass throws;
 * 2. it **does not start at all** when DocuSign is unconfigured on that box
 *    (`isDocuSignConfigured()`), and only a restart changes that;
 * 3. ten at a time means a backlog takes longer than one interval.
 *
 * So *"about five minutes"* is the ordinary case and **not a guarantee**. This
 * sentence says likely, never a deadline, and it never says *live* — there is
 * still no webhook, and {@link STATUS_IS_LAST_KNOWN_NOTE} still holds.
 *
 * ⚠️ **What this does NOT license.** It does not make a status live, it does not
 * make Check status pointless — that button is how somebody gets an answer *now*
 * — and it must never be worded as a promise the poller cannot keep.
 */
export const BACKGROUND_CHECK_NOTE =
  'The server also re-checks on its own, but it is a periodic pass rather than a live feed. Check status for an answer now.';

/**
 * Why a second Send is refused while one envelope is still out.
 *
 * `POST /docusign/envelopes` answers a **409 naming the count** when the quote
 * already carries a non-terminal envelope — one live envelope per quote. So
 * disabling ahead of it is an OPTIMISATION and the refusal is the guarantee;
 * what the optimisation buys is the reason being on screen rather than
 * discovered by pressing. Without it a double-pressed Send puts two contracts in
 * front of one customer, both equally legitimate-looking.
 *
 * It lives here rather than at a call site because two surfaces now offer Send —
 * the quote's `EnvelopePanel` and the row action on `/quotes` — and a sentence
 * about server behaviour written twice is two things to drift.
 *
 * ⚠️ `EnvelopePanel` still holds an inline copy of this string and is
 * deliberately NOT edited alongside it: that file had uncommitted work from
 * another session when this landed. Pointing it at this constant is a one-line
 * change and is the remaining half.
 */
export const ONE_ENVELOPE_PER_QUOTE_REASON =
  'A signature request is already outstanding on this quote. Void it before sending another.';

/* ── Delivery: a real field, and a signing URL only exists for one of them ── */

/**
 * How the signer receives the envelope. **A real field on the record, never an
 * inference**, and it is fixed at send time.
 *
 * - `embedded` — we mint a single-use signing URL and deliver it ourselves.
 *   **DocuSign emails nobody.**
 * - `email` — DocuSign emails the signer directly. There is **no signing URL to
 *   mint**, and asking for one is a `409 DOCUSIGN_ENVELOPE_IS_EMAILED`.
 *
 * DocuSign itself decides delivery by the *presence* of a `clientUserId` on the
 * recipient. The backend made it an explicit field rather than leaving that as
 * an inference, because at a call site the implicit version reads as an accident
 * rather than a choice — and getting it wrong the quiet way means a real
 * customer receives an email nobody meant to send.
 *
 * **Defaults to `embedded` when omitted on a send.** A caller who says nothing
 * about delivery must not have an email reach their customer as a side effect.
 */
export type DocuSignDelivery = 'embedded' | 'email';

/** Structural minimum for the delivery predicates. */
export interface HasDelivery {
  delivery: DocuSignDelivery;
}

/** An envelope proven, at the type level, to be embedded rather than emailed. */
export type EmbeddedDelivery<T extends HasDelivery> = T & { delivery: 'embedded' };

/**
 * The narrowest thing `api.docusignSigningUrl()` will accept.
 *
 * ⚠️ **This is the structural guard, and it is the point of the whole delivery
 * section.** The method takes an envelope rather than an id, and demands
 * `delivery: 'embedded'` — so a call site holding a `DocuSignEnvelope` whose
 * delivery is still the union **cannot compile** until it narrows with
 * {@link isEmbeddedDelivery}. Asking for a signing URL on an emailed envelope is
 * a `409` that cannot be reached from this app by accident.
 *
 * Same pattern, and the same reason, as `DealFormModal` keeping `projectId` out
 * of `FormValues` entirely: **a payload that cannot express the mistake beats a
 * guard somebody can forget.**
 */
export interface SignableEnvelopeRef {
  id: number;
  delivery: 'embedded';
}

/** Narrows an envelope to one a signing URL can be minted for. */
export function isEmbeddedDelivery<T extends HasDelivery>(
  envelope: T,
): envelope is EmbeddedDelivery<T> {
  return envelope.delivery === 'embedded';
}

/**
 * The `error.code` on the 409 refusing a signing URL for an emailed envelope.
 *
 * **Switch on the code, never on the message** — the same rule as the Xero
 * refusals. A distinct code rather than a bare 409 because this one is *not a
 * retry*: the signer already has the document, in their inbox.
 */
export const DOCUSIGN_ENVELOPE_IS_EMAILED = 'DOCUSIGN_ENVELOPE_IS_EMAILED';

/** Why a signing URL cannot be minted, when it cannot. */
export type SigningUrlRefusal = 'emailed' | 'terminal';

/**
 * The runtime half of the same question, for copy and for disabling a control.
 *
 * The type guard above is the guarantee; this is the *explanation*, and both are
 * needed. `terminal` cannot be encoded in a type — it changes under a live
 * record — so the server refuses that case too and this predicate lets a UI say
 * which of the two refusals applies before the user clicks.
 *
 * Returns `null` when a link can be minted. Keep handling the 409 regardless: an
 * envelope can become terminal between the render and the click.
 */
export function signingUrlRefusal(
  envelope: HasDelivery & { terminal: boolean },
): SigningUrlRefusal | null {
  if (envelope.delivery === 'email') return 'emailed';
  if (envelope.terminal) return 'terminal';
  return null;
}

/* ── Reminding: asking DocuSign to email the request again ──────────────── */

/*
 * ⚠️ **THE REMINDER AND THE BACKGROUND POLLER ARE DIFFERENT THINGS, AND THEY
 * SIT ON THE SAME PANEL.**
 *
 *   the POLLER    READS status, on a ~5 minute floor, and CANNOT SEND ANYTHING.
 *                 See {@link BACKGROUND_CHECK_NOTE}.
 *   the REMINDER  EMAILS A CUSTOMER, only when a person presses it, and DOES
 *                 NOT ADVANCE THE STAGE.
 *
 * They will be conflated, because they are adjacent and both are about an
 * envelope nobody is watching. The specific failure to avoid: a sentence saying
 * *"we check every five minutes"* rendered beside a Resend button reads as *"we
 * remind every five minutes"* — a promise to mail a customer on a timer, which
 * nothing does and nothing should. **Never put a cadence and this control in one
 * sentence**, and never word a reminder as something the system does on its own.
 */

/**
 * `POST /docusign/envelopes/{id}/remind` sends an **empty, `.strict()` body**.
 *
 * Who gets reminded is decided by DocuSign from the envelope's own recipients;
 * there is nothing for a caller to choose. Recorded here because the shape is
 * the interesting part: an empty strict body still has to be SENT as `{}` — the
 * same reason `POST /quotes/{id}/convert` sends one explicitly rather than
 * leaning on a server-side default for a missing body.
 */
export const REMIND_ENVELOPE_BODY = {} as const;

/**
 * The envelope is `completed`, `declined` or `voided`. **One code for all three**
 * — the backend's rule is that a refusal earns its own code when it routes
 * somewhere the others do not, and all three route to the same place: there is
 * nobody left to remind, and a quote that still needs signing needs a NEW
 * envelope. Which of the three it is travels in `details.status`.
 *
 * ⚠️ **DocuSign itself does NOT refuse this**, which is the whole reason the
 * guard is server-side. `?resend_envelope=true` on a completed envelope answers
 * `200` having notified nobody — the same silent-success shape as an unmatched
 * template role.
 */
export const DOCUSIGN_ENVELOPE_NOT_OUTSTANDING = 'DOCUSIGN_ENVELOPE_NOT_OUTSTANDING';

/**
 * The envelope was delivered as an embedded signing link, so DocuSign never
 * emailed the signer and has nothing to re-send.
 *
 * ⚠️ **The exact mirror of {@link DOCUSIGN_ENVELOPE_IS_EMAILED}**, and refused
 * for the symmetrical reason: DocuSign does not email a recipient carrying a
 * `clientUserId`, so a reminder on an embedded envelope is accepted upstream and
 * **mails nobody**. Its own code rather than sharing the terminal one, because it
 * routes somewhere else entirely: the envelope is alive and the remedy is to hand
 * the signer a link, not to send anything new.
 */
export const DOCUSIGN_ENVELOPE_IS_EMBEDDED = 'DOCUSIGN_ENVELOPE_IS_EMBEDDED';

/**
 * Pressed again inside the cooldown. **A `429`, not a `409`** — and it is the one
 * refusal here that clears on its own, which is the only thing the codes buy that
 * the sentences cannot express.
 *
 * ⚠️ **The cooldown is NOT a rate limiter and must never be described as one.**
 * It is derived from `f_docusign_envelopes.last_reminded_at` — a fact about the
 * ENVELOPE — so it holds across two salespeople, two browsers and two sessions,
 * which is exactly what a per-IP bucket lets through.
 */
export const DOCUSIGN_REMINDED_TOO_RECENTLY = 'DOCUSIGN_REMINDED_TOO_RECENTLY';

/**
 * ⚠️ **A `200` from a reminder is NOT a delivery**, and this is the sentence that
 * stops a surface claiming one.
 *
 * It means DocuSign accepted the request. There is no webhook, no receipt, and
 * nothing downstream that could ever tell this app whether mail arrived — the
 * envelope simply comes back with `lastRemindedAt` moved, and **nothing in the
 * response says "reminded"**. Rendering it as *"the customer has been chased"* is
 * a claim built from a `2xx` in somebody else's system, which is the mistake this
 * repository has made three times on Xero.
 */
export const REMINDER_IS_NOT_DELIVERY_NOTE =
  'DocuSign accepted the request. Nothing here can confirm the email arrived.';

/** Why a reminder cannot be sent, when it cannot — before anybody presses it. */
export type RemindRefusal = 'terminal' | 'embedded';

/**
 * The runtime half of "can this envelope be reminded", asked before the click.
 *
 * Same shape and same job as {@link signingUrlRefusal}, and the two are near
 * mirror images: a link can be minted for an **embedded, non-terminal** envelope,
 * a reminder can be sent for an **emailed, non-terminal** one. Terminal is tested
 * FIRST in both, matching the server's own ordering — cheapest and most permanent
 * first, so a completed envelope says so rather than saying "try again in nine
 * minutes", which is advice that will never become true.
 *
 * Returns `null` when a reminder can be sent. **Keep handling the refusals
 * regardless**: an envelope can become terminal between the render and the click,
 * and the cooldown is not visible from here at all — see
 * {@link DOCUSIGN_REMINDED_TOO_RECENTLY}.
 */
export function remindRefusal(
  envelope: HasDelivery & { terminal: boolean },
): RemindRefusal | null {
  if (envelope.terminal) return 'terminal';
  if (envelope.delivery !== 'email') return 'embedded';
  return null;
}

/**
 * What the cooldown `429` carries, once every field has been proven.
 *
 * Each member is independently optional because each is guarded independently — a
 * server that stops sending one must not take the other two with it. Modelled on
 * `ResendCooldown` in `@/lib/company-approval`, deliberately: the two cooldowns
 * are the same shape and a reader who knows one knows this one.
 */
export interface ReminderCooldown {
  /** Whole seconds until another reminder is allowed. Non-negative. */
  retryAfterSeconds: number | null;
  /** An ISO timestamp, already proven to be a parseable date. */
  nextAllowedAt: string | null;
  /** An ISO timestamp, already proven to be a parseable date. */
  lastRemindedAt: string | null;
}

/**
 * A timestamp is only useful once it is known to render — an unparseable string
 * formats as `Invalid Date`, which reads as a bug in the app rather than as a
 * server that sent something odd.
 */
function isoOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  return Number.isNaN(Date.parse(trimmed)) ? null : trimmed;
}

/**
 * The shape this module needs from an `ApiError` to read a refusal.
 *
 * Structural rather than importing `ApiError` from `@/lib/api`, because this
 * module sits at the BOTTOM of the import graph — `@/lib/types` imports from it —
 * and `@/lib/api` imports `@/lib/types`. Importing the class here would close the
 * cycle.
 */
export interface RefusalSignals {
  code: string | null;
  message: string;
  /** `ApiError.detailPayload` — the raw `details`, `unknown` and never cast. */
  detailPayload: unknown;
}

/**
 * Read the cooldown facts off the `429`.
 *
 * ⚠️ **`details` is an OBJECT here, not the per-field array**, so
 * `ApiError.details` is `undefined` and the payload sits on `detailPayload` as
 * `unknown`. Guarded field by field and **never cast**, exactly as `xeroMessages`
 * and `detailReasons` are: a cast lets a missing key reach a renderer as
 * `undefined` and print as nothing, or reach arithmetic as `NaN` and print a
 * countdown of `NaN seconds`.
 *
 * ⚠️ **And never parse the sentence.** The retry time appears in the server's
 * prose too, and lifting it out of there is matching on wording by another name —
 * the message is free to be reworded, the fields are the contract.
 *
 * Returns `null` when nothing usable was carried, which is an ordinary answer: a
 * caller then renders the server's own sentence with no countdown rather than
 * inventing one.
 */
export function reminderCooldownOf(error: RefusalSignals): ReminderCooldown | null {
  const payload = error.detailPayload;
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;
  const record = payload as Record<string, unknown>;

  const rawSeconds = record.retryAfterSeconds;
  const retryAfterSeconds =
    typeof rawSeconds === 'number' && Number.isFinite(rawSeconds) && rawSeconds >= 0
      ? Math.ceil(rawSeconds)
      : null;

  const cooldown: ReminderCooldown = {
    retryAfterSeconds,
    nextAllowedAt: isoOrNull(record.nextAllowedAt),
    lastRemindedAt: isoOrNull(record.lastRemindedAt),
  };

  return cooldown.retryAfterSeconds === null &&
    cooldown.nextAllowedAt === null &&
    cooldown.lastRemindedAt === null
    ? null
    : cooldown;
}

/**
 * A reminder refusal, classified.
 *
 * `terminal` says whether the control should come back on its own — the cooldown
 * clears, the other two never do. It is the one thing the codes buy that the
 * sentences cannot express, and it is why they are matched at all.
 */
export interface RemindRefusalDetail {
  kind: 'not-outstanding' | 'embedded' | 'too-recently';
  terminal: boolean;
  /** The server's own sentence. Render it; do not replace it. */
  message: string;
  /** Present only on `too-recently`, and only when the `429` carried usable fields. */
  cooldown: ReminderCooldown | null;
}

/**
 * Classify a reminder refusal by `error.code`.
 *
 * ⚠️ **Switch on the code, NEVER on message text.** All three sentences are the
 * server's to reword, and two of them name a timestamp.
 *
 * ⚠️ **An unrecognised code returns `null`, and the caller MUST fall back to the
 * server's own message.** `null` means *"newer than this map"*, never *"not a
 * refusal"* — the same designed degradation as `quoteRefusalOf` in `@/lib/xero`
 * and `resendRefusalOf` in `@/lib/company-approval`. Do not give it a default
 * classification: guessing `terminal` wrong in the `true` direction takes a
 * working button away for good.
 */
export function remindRefusalOf(error: RefusalSignals): RemindRefusalDetail | null {
  switch (error.code) {
    case DOCUSIGN_ENVELOPE_NOT_OUTSTANDING:
      return { kind: 'not-outstanding', terminal: true, message: error.message, cooldown: null };
    case DOCUSIGN_ENVELOPE_IS_EMBEDDED:
      return { kind: 'embedded', terminal: true, message: error.message, cooldown: null };
    case DOCUSIGN_REMINDED_TOO_RECENTLY:
      return {
        kind: 'too-recently',
        terminal: false,
        message: error.message,
        cooldown: reminderCooldownOf(error),
      };
    default:
      return null;
  }
}

/**
 * How long to wait, in seconds, given whatever the `429` carried.
 *
 * `retryAfterSeconds` is preferred because it is a **duration** on the wire and
 * therefore immune to a skewed browser clock; `nextAllowedAt` is an absolute
 * instant and a clock five minutes fast would show a countdown ending before the
 * server agrees. `null` when neither is usable, so the caller renders the refusal
 * with no countdown rather than inventing one.
 */
export function remindWaitSeconds(cooldown: ReminderCooldown | null): number | null {
  if (!cooldown) return null;
  if (cooldown.retryAfterSeconds !== null) return cooldown.retryAfterSeconds;
  if (cooldown.nextAllowedAt === null) return null;
  const remaining = Math.ceil((Date.parse(cooldown.nextAllowedAt) - Date.now()) / 1000);
  return Number.isFinite(remaining) && remaining > 0 ? remaining : null;
}

/* ── Changing the signer: there is NO recipient-edit endpoint ───────────── */

/*
 * ⚠️ **MEASURED IN THE BACKEND 2026-08-31: `/docusign/envelopes/:id/recipients`
 * IS A `GET` AND ONLY A `GET`.** `docusign.routes.ts` mounts it with
 * `docusignRoutes.get(...)`, the controller exposes `recipients` as a read, and
 * `api.listDocusignRecipients` is the matching client method. **There is no
 * `POST`, no `PATCH`, and nothing that edits a recipient on a live envelope.**
 *
 * This is written down because the opposite was believed and acted on: a brief
 * for this very change stated that the `POST` existed and was already wired. It
 * is the *absence* claim this codebase keeps getting wrong, arriving from the
 * other direction — a positive claim about an endpoint nobody had called.
 *
 * **So "change the signer" is VOID-AND-RESEND**, and that is a different
 * operation with consequences a UI has to state before it runs — see
 * {@link CHANGE_SIGNER_IS_VOID_AND_RESEND}. Do not build a control that implies
 * the recipient on the existing envelope is being edited.
 */

/**
 * What changing the signer actually costs, said before it happens.
 *
 * Every clause is a consequence the reader cannot see and cannot undo:
 *
 *  - **DocuSign emails the original signer** that the envelope was voided,
 *    quoting the reason — which is why the reason is collected rather than
 *    defaulted. The customer reads it.
 *  - **Any signature already on the envelope is discarded.** A part-signed
 *    envelope cannot be re-pointed at somebody else; a fresh one starts empty.
 *  - **A signing link already in the original signer's inbox stops working.**
 *
 * ⚠️ **This is exactly what a REMINDER does not do**, and the two controls sit
 * next to each other: a reminder leaves the envelope, the link and any partial
 * signatures untouched. Anyone who wants the same person chased wants the
 * reminder, and the wording has to make that obvious or they will void an
 * envelope to achieve nothing.
 */
export const CHANGE_SIGNER_IS_VOID_AND_RESEND =
  'DocuSign cannot re-point a live envelope at somebody else. This voids the current request — ' +
  'the original signer is emailed the reason and any signing link they hold stops working — and ' +
  'then sends a fresh one.';

/* ── Who can be a signer ────────────────────────────────────────────────── */

/**
 * ⚠️ **A SIGNER MUST HAVE AN EMAIL ADDRESS, whatever the delivery mode.**
 *
 * It is not only how an `email`-delivery envelope reaches them: DocuSign MATCHES
 * the recipient on name and address, so an embedded signing URL is minted against
 * that pair too. An empty address does not fail loudly — it produces an envelope
 * whose recipient nobody can be, which is the recipient-less-envelope shape this
 * surface already refuses on the template role.
 *
 * ⚠️ **And a contact with no email is the COMMON case here, not the edge.** Only
 * ~16% of the Xero contacts carry one, so a picker over a company's contacts will
 * routinely offer people who cannot sign. They are therefore **not offered as
 * options at all** — an option whose only outcome is a refusal is the
 * affordance-with-one-outcome this codebase forbids on the Xero `sync` button —
 * and their absence is ACCOUNTED FOR in words with a count, because a colleague
 * silently missing from a list reads as a colleague who is not on file.
 */
export const SIGNER_NEEDS_EMAIL =
  'A signer needs an email address — DocuSign matches the recipient on it.';

/**
 * The signer, as a form holds it.
 *
 * ⚠️ **`contactId` NEVER REACHES THE WIRE.** Neither send schema declares it and
 * both are `.strict()`, so a payload builder that spread this object would be a
 * `422` naming a key the user never typed into. Every builder reads `name` and
 * `email` key by key, and says so at the site.
 *
 * It is carried anyway because a seeded value has to SELECT its option rather
 * than merely fill the boxes beside it: an id with no matching option renders as
 * "Unassigned" over a form that is holding one, which is the retired-picker
 * trap this codebase has been bitten by repeatedly. `null` means the address was
 * typed by hand — a signer who is not a contact, which is an ordinary state and
 * not a fallback.
 *
 * ⚠️ **Name and email are ONE choice, not two fields that happen to sit
 * together.** Picking a contact writes both in a single change; a patch that set
 * one alone would leave a recipient DocuSign cannot match, and a mismatch there
 * answers `UNKNOWN_ENVELOPE_RECIPIENT`, which reads as "no such signer" and means
 * "you have them recorded differently".
 *
 * It lives HERE rather than beside the field that renders it because three
 * components pass it around — the send dialog, the change-signer dialog and the
 * panel between them — and a shape shared by three consumers belongs in the
 * module that already owns this integration's model.
 */
export interface SignerValue {
  /** The contact this signer is, or `null` when the address was typed by hand. */
  contactId: number | null;
  name: string;
  email: string;
}

/** Nobody chosen. Distinct from *not passed*, which is what lets a seed fire. */
export const EMPTY_SIGNER: SignerValue = { contactId: null, name: '', email: '' };

/* ── More than one signer ───────────────────────────────────────────────── */

/*
 * Owner's decision, 2026-09-01: *"in some cases or in some companies there can
 * be multiple signers … there should be a picker for the number of signers near
 * the signer and an add signer button."*
 *
 * Four rulings came with it and none of them is open:
 *
 *   1. **at most FOUR** signers;
 *   2. **all of them receive it at once**, in any order — there is no
 *      per-signer ordering control and none is to be built;
 *   3. **more than one signer is EMAIL DELIVERY ONLY** — see
 *      {@link MULTIPLE_SIGNERS_ARE_EMAILED};
 *   4. each signer is **independently** a contact at the quote's company or a
 *      hand-typed person, so every row carries its own picker and its own
 *      "Someone else" mode.
 *
 * ⚠️ **The wire shape changed with it.** `POST /docusign/envelopes/documents`
 * takes `signers` — an ARRAY — in place of `signer`, accepts either shape and
 * **never both**, and this app always sends the array. See
 * `DocuSignDocumentEnvelopeInput`.
 */

/**
 * The most signers one envelope may carry. **The server enforces it too**, as a
 * 422 on `signers`; this is what stops a form offering a fifth row that could
 * only ever be refused.
 */
export const MAX_SIGNERS = 4;

/**
 * Why "I deliver the link myself" is unavailable above one signer.
 *
 * ⚠️ **Not a preference and not a validation rule we invented.** A signing URL
 * is single use and is minted against ONE recipient's name and address, so on a
 * two-signer envelope there is no link to hand to the second person — DocuSign
 * has to write to them. Delivery is fixed at send time and cannot be changed
 * afterwards, so this is stated where the choice is made rather than discovered
 * as a `409` later.
 *
 * It names the way back, because a reason a reader cannot act on is half a
 * sentence: the control returns the moment the envelope is down to one signer.
 */
export const MULTIPLE_SIGNERS_ARE_EMAILED =
  'With more than one signer, DocuSign emails each of them their own request. A signing link is ' +
  'single use and identifies one person, so handing the link over yourself needs exactly one ' +
  'signer — remove the others to choose it.';

/**
 * The same address on two rows of one envelope.
 *
 * ⚠️ **DocuSign matches a recipient on name and email**, so two identical
 * recipients is not a duplicate that upstream quietly collapses — it is an
 * envelope whose second signer cannot be told apart from its first, and the
 * failure surfaces later as an `UNKNOWN_ENVELOPE_RECIPIENT`-shaped confusion
 * rather than at the moment somebody typed it.
 *
 * Both ways out are named, because a shared mailbox is a real answer to *"who
 * signs"* and telling somebody only that they cannot use it strands them.
 */
export const DUPLICATE_SIGNER_EMAIL =
  'This address is already on another signer. DocuSign tells recipients apart by name and email, ' +
  'so use a different address or remove one of them.';

/**
 * The key a `422` on one signer arrives under — `signers.0.email`.
 *
 * ⚠️ **Built in ONE place because two independent things have to agree on it
 * character for character**: the form's own validation, and the write-key list
 * `ApiError.routeFieldErrors()` partitions a server refusal against. That match
 * is exact — a key spelled `signer.0.email` in either half lands the refusal in
 * the form-level banner, marked on no control, while still counting towards
 * "one field needs attention, highlighted above".
 */
export function signerErrorKey(index: number, field: 'name' | 'email'): string {
  return `signers.${index}.${field}`;
}

/* ── Copying somebody in ─────────────────────────────────────────────────
 *
 * Owner instruction, 2026-09-16: "i want a feature of adding cc in the signer
 * modal when a quote is sent for signing".
 *
 * ⚠️ **A COPY IS NOT A SIGNER WITH THE SIGNING TURNED OFF**, and the wording
 * below keeps saying so. They are asked for nothing, they are not on the
 * document, and the only thing a reader needs to know is that an email reaches
 * them. Everything the signer constants above say about matching, ordering and
 * links is irrelevant here — which is why this is its own vocabulary rather
 * than four more parameters on theirs.
 * ---------------------------------------------------------------------- */

/**
 * The most addresses one envelope may copy. **The server enforces it too**, as
 * a 422 on `ccEmails`.
 *
 * ⚠️ **IT IS NOT {@link MAX_SIGNERS} AND MUST NOT BE DERIVED FROM IT.** Four
 * signers is a fact about the PDF — four numbered `Signature` / `Name` / `Date`
 * blocks are what fit the quote terms without spilling onto a second page. A
 * copy is rendered into the document nowhere at all, so nothing about the page
 * constrains this number and the two agreeing would be a coincidence.
 *
 * ⚠️ **It mirrors `MAX_ENVELOPE_CCS` in the API, which this app cannot import.**
 * Moving one without the other gives a form that offers a row the server
 * refuses, or refuses a row the server would take.
 */
export const MAX_CCS = 5;

/**
 * Somebody listed both as a signer and as a copy.
 *
 * ⚠️ **Refused rather than de-duplicated, and the reason is not tidiness.**
 * DocuSign matches a recipient on name and email, so the same address in both
 * lists is two recipients wearing one identity — the failure
 * {@link DUPLICATE_SIGNER_EMAIL} describes, reached from the other direction.
 *
 * It says why nothing is lost by the refusal, because a rule a reader cannot
 * see the point of is a rule they work around: a signer is sent the completed
 * document by DocuSign anyway, so copying them adds nothing.
 */
export const SIGNER_CANNOT_BE_COPIED =
  'This address is already signing. DocuSign sends every signer the completed document, so there ' +
  'is nothing to add by copying them as well.';

/** The same address on two copy rows. Same upstream reason as the signer one. */
export const DUPLICATE_CC_EMAIL =
  'This address is already being copied. Remove one of the two.';

/**
 * The key a `422` on one copied address arrives under — `ccEmails.0`.
 *
 * ⚠️ **ONE LEVEL, not two, and that is the difference from
 * {@link signerErrorKey}.** `ccEmails` is a flat `string[]`, so the server
 * names the INDEX and there is no field beneath it. A key spelled
 * `ccEmails.0.email` here would match nothing and drop the refusal into the
 * form-level banner, marked on no control, while still counting towards "one
 * field needs attention, highlighted above".
 */
export function ccErrorKey(index: number): string {
  return `ccEmails.${index}`;
}

/**
 * What copying somebody actually does, said once.
 *
 * ⚠️ **THIS IS A SENTENCE BESIDE A CONTROL, AND THE NO-HELPER-TEXT PROHIBITION
 * IS NOT BEING CARVED OUT — THE TEST WAS APPLIED.** *What wrong conclusion does
 * it prevent?* That copying a colleague is a quiet, internal, after-the-fact
 * note. It is not: **an email leaves for a real person the moment Send is
 * pressed**, and nothing else on this screen says so. That is the same class as
 * the Xero-push and sandbox warnings the rule protects by name — a consequence
 * of an outward-facing action that the control itself cannot state.
 *
 * ⛔ **That licence covers this sentence and nothing else in the section.** No
 * hint on the address box, no note under the Add control.
 *
 * ⚠️ **It names the TIMING, because that was the owner decision and it is the
 * only part a reader cannot guess.** They are written to when the envelope goes
 * out — not held back until it is signed — and they receive the completed
 * document at the end as well. The alternative was offered and declined, so a
 * later pass must not soften this to "they get a copy" and lose which.
 *
 * ⛔ **It does not promise they can see the signing progress, or that they can
 * do anything at all.** A copy recipient is a mailbox on a list.
 */
export const CC_EXPLAINER =
  'Copied people are emailed when this goes out and again once it is signed. They are not asked ' +
  'to sign anything.';

/**
 * The sentence naming contacts that were left out of the signer picker.
 *
 * ⚠️ **Says where the fix is, and offers the way past it in the same breath.**
 * Either half alone is advice that cannot be followed: "add an address on the
 * contact record" strands somebody who is on the phone right now, and "type one
 * below" leaves the directory permanently wrong.
 *
 * `null` at zero, so a caller renders nothing rather than "0 contacts have no
 * email address" — a true sentence about nothing, which reads as a fault.
 */
export function contactsWithoutEmailNote(count: number): string | null {
  if (count <= 0) return null;
  const subject =
    count === 1
      ? 'One of this company’s contacts has'
      : `${count} of this company’s contacts have`;
  return (
    `${subject} no email address, so they are not listed. ${SIGNER_NEEDS_EMAIL} ` +
    'Add one on the contact record, or enter an address below.'
  );
}

/* ── What a completed signature actually does ───────────────────────────── */

/**
 * ⚠️ **A completed signature ACCEPTS THE QUOTE.** Not "records a signature" —
 * the backend's refresh hook moves the quote to the accepting stage and
 * attaches the signed PDF to the Xero quote.
 *
 * **Nobody is asked to confirm.** The customer signing IS the confirmation, and
 * there is nobody at a keyboard to ask. So **the last moment a human can decide
 * is when the envelope is SENT**, which is why this sentence belongs on the send
 * screen and not only on a status view.
 *
 * ⚠️ **Acceptance does not raise an invoice or touch Xero.** Accepting a quote,
 * by any route, moves the stage and does nothing else. No invoice is raised,
 * nothing reaches Xero, and there is no confirmation step guarding it.
 *
 * Raising an invoice from an accepted quote is a person's job — Convert, or
 * billing the quote's line items — and pushing that invoice to Xero is done from
 * the invoice's own page.
 *
 * ── ⚠️ "AUTOMATICALLY": DECIDED, 2026-08-30, AND DELIBERATELY LEFT ALONE ──
 *
 * This string has FOUR render sites and is the single copy of a claim about
 * server behaviour, so the word was re-put rather than edited in passing.
 *
 * It was always a claim about **consent** — nobody is asked, the customer
 * signing is the confirmation — and never about **timing**. That reading is
 * unchanged. What changed is that the gap between the two readings **narrowed**:
 * a background poll now runs the same hook, so "automatically" is closer to how
 * a reader will take it than it was when only a human could trigger it. Editing
 * it would be correcting a word in the direction it has just moved away from.
 *
 * ⚠️ **So TIMING is carried by the sentence NEXT TO IT, never by this one** —
 * {@link BACKGROUND_CHECK_NOTE}, with its three caveats. Do not fold a
 * timing claim in here: this string renders in places that state no timing at
 * all, and a deadline inherited by four surfaces at once is exactly how a
 * promise ends up somewhere nobody checked it.
 *
 * What did NOT change: **there is still no webhook.** DocuSign pushes nothing,
 * and a status is only ever as current as the last check — see
 * {@link STATUS_IS_LAST_KNOWN_NOTE}.
 *
 * ⚠️ **SUPERSEDED 2026-08-30, and quoted rather than deleted because it is what
 * a git blame or a stale doc will show:** *"The stage only moves when somebody
 * calls `POST /docusign/envelopes/{id}/refresh`, so a quote can sit signed at
 * DocuSign and unaccepted here indefinitely."* The backend's status worker
 * falsified the second half — it runs the same refresh on a timer. The first
 * half was never wrong about the ROUTE; it was wrong that a person is the only
 * caller of it.
 */
export const SIGNATURE_ACCEPTS_QUOTE_NOTE =
  'A completed signature moves this quote to the accepted stage. Nobody is asked to confirm.';

/**
 * The sentence a send screen must show, given the connection.
 *
 * ⚠️ **Both halves are true at once, which is the whole hazard**, and the
 * mechanism is unchanged: **nothing on the acceptance path reads the
 * environment.** So a sandbox signature — carrying no legal force — still moves
 * a real quote to the accepted stage, and nothing anywhere records that the
 * envelope was a sandbox one.
 *
 * ⚠️ *SUPERSEDED 2026-08-31: "it is a live one rather than a hypothetical: the
 * server credentials are the DocuSign SANDBOX on production, deliberately".*
 * **DocuSign was switched to production**, so this is no longer the everyday
 * state — which is exactly why the sentence is still generated from the
 * connection rather than hardcoded. It fires when a reading says non-production
 * and stays silent otherwise; see {@link signaturesNotBinding}, whose null case
 * reversed on the same day.
 *
 * ⚠️ **Acceptance does not raise an invoice or touch Xero**, so a sandbox
 * signature's only real consequence is the stage move — not a draft in the
 * accountant's books. The *sandbox* warning itself still stands: a demo
 * envelope still comes back marked signed and is still worth nothing, and that
 * failure still looks exactly like success.
 *
 * Written as one function rather than two adjacent strings because
 * `formError ?? count` is this repo's recurring silent-drop bug: **if both sides
 * can be true at once, rendering one of them is the defect.** Both sides are
 * still true at once — the signature carries no legal force *and* the stage move
 * it causes is real — so the shape is unchanged.
 */
export function signatureConsequenceNote(
  connection: DocuSignBindingSignals | null | undefined,
): string {
  if (!signaturesNotBinding(connection)) return SIGNATURE_ACCEPTS_QUOTE_NOTE;
  return `${SIGNATURE_ACCEPTS_QUOTE_NOTE} The signature carries no legal force here, but the stage move is real.`;
}

/* ── The return URL, and what it does not prove ─────────────────────────── */

/**
 * The query parameter DocuSign appends to a return URL when a signer finishes.
 *
 * ⚠️ **Seeing it is NOT proof of a signature.** It is a client-side redirect the
 * signer's browser performs, and the signer can edit the address bar — so
 * anything recording a signature on the strength of it is recording something a
 * customer can assert about themselves. Call `POST /docusign/envelopes/{id}/refresh`
 * and read `status` instead. {@link signingReturnIsProof} exists to say so at the
 * one place someone would otherwise be tempted.
 */
export const SIGNING_COMPLETE_EVENT = 'signing_complete';

/**
 * **Always `false`.** A deliberately useless function, so that the temptation to
 * treat `?event=signing_complete` as an outcome meets a name that answers it.
 *
 * The return event tells you the signer's browser came back and nothing else —
 * useful for closing a dialog and triggering a refresh, never for recording a
 * result. There is no webhook, so `/refresh` is the only source of truth.
 */
export function signingReturnIsProof(): false {
  return false;
}

/**
 * How an envelope's signers read in one line of prose.
 *
 * ⚠️ IT FALLS BACK TO `signer`, NEVER TO AN EMPTY LIST. The two repos deploy
 * independently, so a response from a server without `signers` must degrade to
 * yesterday's single name rather than to nothing -- and a server that cannot
 * send the field cannot have created a multi-signer envelope, so the fallback
 * can never under-report one.
 *
 * Shared rather than written at each call site: three surfaces name the signers
 * (the integrations card, the void dialog, the change-signer dialog) and three
 * copies is three chances to say "1 signers".
 */
export function signerSummary(envelope: {
  signer: { name: string; email: string };
  signers?: { name: string; email: string }[];
}): string {
  const list =
    Array.isArray(envelope.signers) && envelope.signers.length > 0
      ? envelope.signers
      : [envelope.signer];
  const first = list[0];
  const lead = first ? first.name || first.email : 'The signer';
  if (list.length === 1) return lead;
  return `${lead} and ${list.length - 1} ${list.length === 2 ? 'other' : 'others'}`;
}

/** How many people this envelope asks to sign. Always at least one. */
export function signerCount(envelope: { signers?: unknown[] }): number {
  return Array.isArray(envelope.signers) && envelope.signers.length > 0
    ? envelope.signers.length
    : 1;
}
